/* PDP SS25 (guía págs. 23-25): selección de talla, indicador de galería móvil,
   popup de tecnologías, guía de tallas, favoritos y CTA fijo en móvil.

   No usa el <product-info> de Dawn: como cada producto es un solo color y la
   única opción es la talla, cambiar de talla no cambia la página, así que basta
   con actualizar precio, SKU y el id de variante del form. Evita el re-render
   por fetch de sección de Dawn, que aquí sería puro peso. */
(() => {
  const init = (root) => {
    if (root.dataset.tblPdpReady === 'true') return;
    root.dataset.tblPdpReady = 'true';

    const variantsTag = root.parentNode.querySelector('[data-tbl-variants]');
    let variants = [];
    try {
      variants = JSON.parse(variantsTag?.textContent || '[]');
    } catch {
      variants = [];
    }

    const money = (cents) => {
      // Se toma el formato de la propia página (Q1,899.00) en lugar de reimplementarlo.
      const sample = root.querySelector('[data-tbl-price-current]')?.textContent.trim() || '';
      const symbol = sample.replace(/[\d.,\s]/g, '') || 'Q';
      return symbol + (cents / 100).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    /* --- Talla --- */
    const variantInput = root.querySelector('[data-tbl-variant-id]');
    const atc = root.querySelector('[data-tbl-atc]');
    const priceCurrent = root.querySelector('[data-tbl-price-current]');
    const priceCompare = root.querySelector('[data-tbl-price-compare]');
    const priceWrap = root.querySelector('[data-tbl-price]');
    const skuEl = root.querySelector('[data-tbl-sku]');
    const stickyPrice = root.querySelector('[data-tbl-sticky-price]');

    const applyVariant = (variant) => {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      if (priceCurrent) priceCurrent.textContent = money(variant.price);
      if (stickyPrice) stickyPrice.textContent = money(variant.price);

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (priceWrap) priceWrap.classList.toggle('tbl-pdp__price--sale', !!onSale);
      if (priceCompare) {
        priceCompare.hidden = !onSale;
        if (onSale) priceCompare.textContent = money(variant.compare_at_price);
      }
      if (skuEl && variant.sku) skuEl.textContent = variant.sku;

      if (atc) {
        const label = atc.querySelector('span');
        if (variant.available) {
          atc.removeAttribute('aria-disabled');
          if (label) label.textContent = 'AGREGAR AL CARRITO';
        } else {
          atc.setAttribute('aria-disabled', 'true');
          if (label) label.textContent = 'AGOTADO';
        }
      }
    };

    root.querySelectorAll('[data-tbl-size]').forEach((input) => {
      input.addEventListener('change', () => {
        const variant = variants.find((v) => String(v.id) === String(input.value));
        applyVariant(variant);
      });
    });

    /* --- Galería móvil: loop infinito + indicador de posición (guía SS25 pág. 25) ---
       Solo aplica <750px (breakpoint móvil de este theme, no el de 990px al que
       la grid de escritorio reemplaza el carril): entre 750-989px sigue viviendo
       el mismo carril con scroll-snap pero SIN loop, así que ahí se conserva el
       indicador finito de siempre.

       Técnica elegida: clonar el primer y el último slide y ponerlos en los
       extremos ([clonÚltimo, real0..realN-1, clonPrimero]). Al arrancar se salta
       sin animación al real0 (que ahora ocupa la posición 1). Cuando el usuario
       desliza hasta un clon y el carril se asienta, se reposiciona con
       `scrollTo({behavior:'auto'})` al slide real equivalente: el salto es
       invisible porque el clon es pixel-idéntico al real que sustituye.
       Alternativa descartada: reposicionar sin clones (saltar de N-1 a 0 al
       llegar al final) se ve mal aquí porque scroll-snap anima ese salto como
       un scroll normal de N-1 posiciones; con clones el salto real es de un
       solo slide de ancho, indistinguible del scroll normal.

       Los dots SIEMPRE reflejan el índice real: cada slide (real o clon) lleva
       `data-tbl-real-index`, y el clon hereda el del original vía cloneNode.

       Con 0 o 1 imagen no hay nada que recorrer: ni Liquid renderiza los dots
       (`product.media.size > 1`) ni este bloque monta el loop, así que el
       comportamiento queda igual que antes.

       prefers-reduced-motion: el reposicionamiento (inicial y de loop) usa
       SIEMPRE behavior:'auto', nunca 'smooth' — no hay animación de snap que
       desactivar porque nunca se agrega una. El scroll-snap nativo del carril
       (gesto táctil) es ajeno a esta preferencia y no cambia con la tarea. */
    const gallery = root.querySelector('[data-tbl-gallery]');
    const dots = [...root.querySelectorAll('[data-tbl-dots] .tbl-pdp__dot')];
    if (gallery) {
      const realSlides = [...gallery.querySelectorAll('[data-tbl-slide]')];

      if (realSlides.length > 1) {
        realSlides.forEach((slide, i) => {
          slide.dataset.tblRealIndex = String(i);
        });

        const mobileMq = window.matchMedia('(max-width: 749px)');
        let cloneFirst = null;
        let cloneLast = null;
        let loopReady = false;

        const positionAtStart = () => {
          const w = gallery.clientWidth;
          if (!w) {
            requestAnimationFrame(positionAtStart);
            return;
          }
          // Instantáneo a propósito (sin scroll-behavior:smooth en este
          // elemento): equivale a behavior:'auto', no hay flash del clon.
          gallery.scrollLeft = w;
        };

        const buildClones = () => {
          if (loopReady) return;
          cloneLast = realSlides[realSlides.length - 1].cloneNode(true);
          cloneFirst = realSlides[0].cloneNode(true);

          [cloneLast, cloneFirst].forEach((clone) => {
            // cloneNode copia data-tbl-slide del original: se quita para que
            // ese selector siga significando "slide real" en cualquier código
            // futuro (el snapshot `realSlides` de aquí arriba no lo necesita,
            // pero mejor no dejar el atributo con doble sentido en el DOM).
            clone.removeAttribute('data-tbl-slide');
            clone.setAttribute('data-tbl-slide-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
          });

          // El clon del último queda pegado ANTES del primero: es el vecino
          // inmediato al arrancar, conviene tenerlo listo (eager). El clon del
          // primero cae al final, lejos del scroll inicial: se deja lazy para
          // no forzar la descarga de todas las imágenes solo por el loop.
          cloneLast.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'eager'));
          cloneFirst.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'));

          gallery.insertBefore(cloneLast, realSlides[0]);
          gallery.appendChild(cloneFirst);
          loopReady = true;
          positionAtStart();
        };

        const teardownClones = () => {
          if (!loopReady) return;
          cloneLast?.remove();
          cloneFirst?.remove();
          cloneLast = null;
          cloneFirst = null;
          loopReady = false;
          gallery.scrollLeft = 0;
        };

        const extendedSlides = () => [...gallery.querySelectorAll('[data-tbl-slide], [data-tbl-slide-clone]')];

        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const real = entry.target.dataset.tblRealIndex;
              if (real === undefined) return;
              const idx = Number(real);
              dots.forEach((d, di) => d.classList.toggle('is-active', di === idx));
            });
          },
          { root: gallery, threshold: 0.6 }
        );

        // El reajuste de loop se dispara al ASENTARSE el carril (scrollend), no
        // en cada evento de scroll ni desde el IntersectionObserver de arriba:
        // así no se interrumpe la inercia/gesto del usuario a medio deslizar.
        let settleTimer = null;
        const onSettled = () => {
          if (!loopReady) return;
          const slideWidth = gallery.clientWidth;
          if (!slideWidth) return;
          const pos = Math.round(gallery.scrollLeft / slideWidth);
          const lastRealPos = realSlides.length; // posición del último real en el carril extendido
          if (pos === 0) {
            gallery.scrollTo({ left: lastRealPos * slideWidth, behavior: 'auto' });
          } else if (pos === lastRealPos + 1) {
            gallery.scrollTo({ left: slideWidth, behavior: 'auto' });
          }
        };

        if ('onscrollend' in window) {
          gallery.addEventListener('scrollend', onSettled);
        } else {
          // Safari/WebViews sin soporte de 'scrollend': se aproxima con un
          // debounce del evento scroll (150ms de quietud = gesto terminado).
          gallery.addEventListener(
            'scroll',
            () => {
              clearTimeout(settleTimer);
              settleTimer = setTimeout(onSettled, 150);
            },
            { passive: true }
          );
        }

        const syncMode = () => {
          if (mobileMq.matches) buildClones();
          else teardownClones();
          io.disconnect();
          extendedSlides().forEach((s) => io.observe(s));
        };

        syncMode();
        mobileMq.addEventListener('change', syncMode);
      }
    }

    /* --- Modales --- */
    const modals = {};
    document.querySelectorAll('[data-tbl-modal]').forEach((m) => {
      modals[m.dataset.tblModal] = m;
      m.querySelectorAll('[data-tbl-modal-close]').forEach((btn) =>
        btn.addEventListener('click', () => closeModal(m))
      );
    });

    let lastFocus = null;
    const openModal = (m) => {
      if (!m) return;
      lastFocus = document.activeElement;
      m.hidden = false;
      document.body.classList.add('tbl-modal-open');
      m.querySelector('.tbl-modal__close')?.focus();
    };
    const closeModal = (m) => {
      if (!m) return;
      m.hidden = true;
      document.body.classList.remove('tbl-modal-open');
      lastFocus?.focus();
    };
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      Object.values(modals).forEach((m) => {
        if (!m.hidden) closeModal(m);
      });
    });

    root.querySelector('[data-tbl-open-sizeguide]')?.addEventListener('click', () => openModal(modals.sizeguide));

    root.querySelectorAll('[data-tbl-tech]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const m = modals.tech;
        if (!m) return;
        m.querySelector('[data-tbl-tech-title]').textContent = btn.dataset.techName || '';
        m.querySelector('[data-tbl-tech-body]').textContent = btn.dataset.techDescription || '';
        openModal(m);
      });
    });

    /* --- Favoritos: en localStorage, sin app ni cuenta de cliente --- */
    const KEY = 'tbl-favoritos';
    const readFavs = () => {
      try {
        return JSON.parse(localStorage.getItem(KEY) || '[]');
      } catch {
        return [];
      }
    };
    const fav = root.querySelector('[data-tbl-fav]');
    if (fav) {
      const id = fav.dataset.tblFav;
      const sync = () => fav.setAttribute('aria-pressed', String(readFavs().includes(id)));
      sync();
      fav.addEventListener('click', () => {
        const favs = readFavs();
        const i = favs.indexOf(id);
        if (i === -1) favs.push(id);
        else favs.splice(i, 1);
        try {
          localStorage.setItem(KEY, JSON.stringify(favs));
        } catch {
          /* modo privado de Safari: el botón sigue respondiendo, no persiste */
        }
        sync();
      });
    }

    /* --- CTA fijo de móvil: aparece una vez que el botón real quedó por encima ---
       Se compara la posición en cada scroll en lugar de usar IntersectionObserver:
       el observer solo dispara cuando CAMBIA el booleano de intersección, y en un
       salto de scroll (ancla, restauración de posición, teclado) el botón pasa de
       "fuera por abajo" a "fuera por arriba" sin cambiarlo, así que el CTA nunca
       aparecía. Bug real detectado en pruebas. */
    const sticky = root.querySelector('[data-tbl-sticky]');
    if (sticky && atc) {
      /* Sin rAF a propósito: con un flag de "ya agendado", si el frame no llega a
         correr el flag queda atascado y la barra se congela (pasó en pruebas). La
         lectura es una sola y solo se escribe cuando el estado cambia. */
      const sync = () => {
        // Visible solo cuando el botón real ya salió por arriba del viewport
        const debeOcultarse = atc.getBoundingClientRect().bottom > 0;
        if (sticky.hidden !== debeOcultarse) sticky.hidden = debeOcultarse;
      };
      window.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
      root.querySelector('[data-tbl-sticky-atc]')?.addEventListener('click', () => atc.click());
    }
  };

  const boot = () => document.querySelectorAll('[data-tbl-pdp]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // El editor de temas recarga secciones sueltas
  document.addEventListener('shopify:section:load', boot);
})();
